import { create } from 'zustand';
import { defaultDtfPipeline, parseColor, type Rgb, type Separation, type Step } from '@eip/engine';
import type { CheckLite, DocMeta, RunResult } from '../worker/protocol';
import { defaultSepDefaults, type AppMode, type DocSep, type SepDefaults } from './sep';
import { defaultMockupDefaults, sanitizeJob, type DocMockup, type GlPreviewState, type MockupDefaults, type SceneStatus, type TemplateStatus } from './mockup';
import type { TemplateSource } from '../lib/templates';
import type { SceneSource } from '../lib/scenes';
import type { Backdrop, MockupJob, MockupView } from '@eip/engine';
import { clampPanelWidth, PANEL_DEFAULT, viewportWidth } from '../lib/panel';
import { forgetUndo, pushUndo, type UndoScope, type UndoToken } from './undo';

export interface Settings { garmentHex: string; targetWidthIn: number | null; dpi: number; presetId: string }
export interface DocEntry {
  id: string; name: string; size: number; status: 'loading' | 'ready' | 'error'; error?: string; meta?: DocMeta;
  /** A pipeline-run failure; distinct from `status`/`error`, which are reserved for open/decode failures. */
  runError?: string;
  run?: RunResult; running: boolean; runningGeneration?: number; checks: CheckLite[]; inspecting: boolean; previewVersion: number;
  /** The token of the inspection whose checks `checks` holds. Separate from the run
   *  generation: two inspections of the same output can overlap, so a streamed check
   *  from a superseded one has to be droppable. */
  inspectGeneration?: number;
  /** Bumped whenever an inspection starts. The worker rebuilds every check mask on
   *  each inspect, so the viewer keys its overlay bitmap cache on this as well as
   *  `previewVersion` — an inspect-only pass changes the masks without changing the
   *  preview, and previously left the previous preset's overlay painted on canvas. */
  inspectVersion: number;
  /** Screen-print separation state; absent until the document's separation is
   *  first touched (mode switch, detect, or an edit). */
  sep?: DocSep;
  /** Garment-mockup state; absent until the document is seeded in mockup mode. */
  mockup?: DocMockup;
}
export interface ViewerPrefs {
  background: 'checker' | 'garment' | 'white';
  view: 'output' | 'source';
  overlays: Record<string, boolean>;
  /** Which screen-print view the canvas shows: `'proof'` (the composite) or a
   *  plate id. A plate that a rebuild removed falls back to the proof where it
   *  is read, so nothing has to write to the store to repair it. */
  plate: string;
  /** Show the selected plate's film at 1:1 instead of its plate preview.
   *  Meaningless (and disabled in the UI) while `plate` is `'proof'`. */
  filmView: boolean;
  /** Draw the mockup's print area as a dashed outline over the garment. On by
   *  default — it is the only thing on the canvas that says where a drag may
   *  place the art — and, like `plate`/`filmView`, deliberately not persisted:
   *  `savePersisted` whitelists the fields it writes. */
  showPrintArea: boolean;
  /** Which half of a front+back pair the canvas shows. Falls back to `'front'`
   *  where it is read whenever there is no back design to show, so nothing has
   *  to write to the store to repair it. */
  mockupView: MockupView;
  /** The side panel's width in px, held to `[PANEL_MIN, maxPanelWidth(window)]`
   *  by `setPanelWidth`. Persisted: a shop that widened the panel to read the
   *  mockup controls should not have to widen it again tomorrow. */
  panelWidth: number;
  /** Which panel sections are folded away, by `Section` id. Absent (or false)
   *  is open, so a section added later starts open for everyone. Persisted. */
  collapsed: Record<string, boolean>;
}

/**
 * A document that has been closed but not yet let go of: its entry, and the
 * index it sat at, so Undo can put it back exactly where it was. Its bitmaps and
 * its worker state are deliberately left alone for as long as it is held — see
 * `closeDoc` in `app/session.ts`, which owns the grace period and frees both
 * when it expires.
 */
export interface ClosedDoc { id: string; index: number; doc: DocEntry }

/** The one line the app says out loud about something it just did, with an Undo
 *  beside it when there is something to undo. One at a time: a second message
 *  replaces the first, because a stack of them over the canvas is worse than the
 *  silence it replaced.
 *
 *  `undoToken` is the undo entry this message is ABOUT — not "whatever is on top
 *  of the stack" — so its Undo button reverses the change it names however many
 *  edits land in the six seconds it is up. Null for a message with nothing to
 *  undo (the confirmation an undo itself leaves behind). */
export interface ToastMessage { id: number; text: string; undoToken: UndoToken | null }

/** The undo key every close of this document shares, so the drop that ends the
 *  grace period can forget an undo it can no longer honour. */
export function closedDocKey(id: string): string { return `close:${id}`; }

export const DEFAULT_SETTINGS: Settings = { garmentHex: '#000000', targetWidthIn: null, dpi: 300, presetId: 'generic' };

export function garmentRgb(hex: string): Rgb { return parseColor(hex); }

export function stepsForGarment(steps: Step[], garmentHex: string): Step[] {
  const g = garmentRgb(garmentHex);
  return steps.map((s) => (s.kind === 'knockout' ? { ...s, params: { ...s.params, garment: g } } : s));
}

/**
 * The tolerance the knockout step knocks the garment colour out at, which the
 * near-garment-colour check must be judged against (a different threshold
 * either flags pixels no knockout would have removed or none at all). Falls
 * back to the default when no knockout step is configured; a disabled step
 * still describes the intent, so its value is used.
 */
export const DEFAULT_GARMENT_TOLERANCE = 24;
export function knockoutTolerance(steps: Step[]): number {
  const k = steps.find((s) => s.kind === 'knockout');
  return k && k.kind === 'knockout' ? k.params.tolerance : DEFAULT_GARMENT_TOLERANCE;
}

/**
 * True when the ONLY thing that changed is the vendor preset. The preset feeds
 * the inspector and nothing in the pipeline, so this change needs a re-inspect
 * rather than a full re-run (~5 s on a large file, for identical pixels).
 */
export function onlyPresetChanged(next: Settings, prev: Settings): boolean {
  return next.presetId !== prev.presetId
    && next.garmentHex === prev.garmentHex
    && next.targetWidthIn === prev.targetWidthIn
    && next.dpi === prev.dpi;
}

export function withResizeStep(steps: Step[], widthIn: number | null, dpi: number): Step[] {
  const rest = steps.filter((s) => s.kind !== 'resize');
  if (!widthIn || widthIn <= 0) return rest;
  return [...rest, { id: 'resize', kind: 'resize', enabled: true, params: { widthIn, dpi } }];
}

/**
 * Reconcile persisted steps with persisted settings at hydration. The settings
 * bar is the single source of truth for print width and dpi (review #4), so a
 * persisted resize step is never trusted on its own: if `targetWidthIn` failed
 * validation and fell back to null, a surviving resize step would resize the
 * output while the bar, the inspector's `targetWidthIn` and the export filename
 * all said native. Re-derives the step from the validated settings instead.
 */
export function hydrateSteps(steps: Step[], settings: Settings): Step[] {
  const next = withResizeStep(steps, settings.targetWidthIn, settings.dpi);
  // The bar owns the resize step's width and dpi, but not its enable toggle:
  // `PipelinePanel` renders one for every step, so a resize step switched off is
  // a real, persisted user choice and must survive a reload. A step that was not
  // persisted at all is added enabled, as `withResizeStep` does everywhere else.
  const persisted = steps.find((s) => s.kind === 'resize');
  if (!persisted || persisted.enabled) return next;
  return next.map((s) => (s.kind === 'resize' ? { ...s, enabled: false } : s));
}

export interface AppState {
  docs: DocEntry[]; activeId: string | null; settings: Settings; steps: Step[]; viewer: ViewerPrefs;
  /** DTF prep or screen-print separations; switches which panel and runner drive the doc. */
  appMode: AppMode;
  /** The shop's last-used separation settings (everything but inks), seeded into
   *  every newly-touched document and persisted across sessions. */
  sepDefaults: SepDefaults;
  /** The shop's last-used mockup settings, seeded into every newly-touched
   *  document and persisted across sessions. */
  mockupDefaults: MockupDefaults;
  /** Every blank the app can render into: the shipped ones, then the user's own.
   *  Loaded once at startup and replaced wholesale when a user template is
   *  saved or deleted. */
  templates: TemplateSource[];
  /** Where each template stands in the worker, keyed by template id. Keyed by
   *  TEMPLATE rather than document because preparation is shared: two documents
   *  on the same blank prepare it once. */
  templateStatus: Record<string, TemplateStatus>;
  /** Every backdrop scene the shop has imported, newest last — the pixels stay in the Blob each
   *  one carries until the worker decodes it. Loaded once at startup and replaced wholesale when a
   *  scene is added or removed. */
  scenes: SceneSource[];
  /** Where each scene stands in the worker, keyed by scene id. Keyed by SCENE rather than document
   *  because the decoded image is shared: two documents on the same backdrop decode it once. */
  sceneStatus: Record<string, SceneStatus>;
  /** A fatal, app-wide failure (worker load, unsupported browser); rendered as a banner. */
  engineError: string | null;
  /**
   * Which renderer the interactive mockup preview is on. Written by `useMockupView` — the only
   * thing that knows whether a GPU frame actually landed — and read by the inspector's Renderer
   * row and by the App banner that owns up to the slower path.
   *
   * `'cpu'` until a GPU frame has been RENDERED, so nothing claims the fast path before it has one.
   * Rendered, not yet painted: the hook writes `'gl'` as soon as `glPreviewRender` hands back a
   * canvas, which can precede the first CPU render and therefore the first GPU pixel on screen (the
   * viewer also needs `mockupOnCanvas`), so the row can read `GPU` over the dimmed DTF stand-in.
   * See `App.tsx` for why the banner waits for more than this value before it appears.
   */
  glPreview: GlPreviewState;
  /**
   * True while a batch (Export all) is zipping. Deliberately NOT a lock on
   * edits: the settings bar and pipeline panel stay live, and the run-trigger
   * subscription keeps re-running docs. What guarantees the archive is one
   * consistent snapshot is the batch's per-doc `run.generation` check (see
   * `lib/batch.ts`), which leaves a doc that moved out of the ZIP with a
   * reason. This flag only tells the UI that one encode is already in flight,
   * so a second export cannot be started alongside it.
   */
  batchBusy: boolean;
  /**
   * Documents closed within the grace period, newest last. A closed document is
   * the one deletion in the app that costs real work to get back (open the file
   * again, wait for the run, place the art again), so `removeDoc` sets it aside
   * here instead of dropping it, and the undo entry it pushes puts this entry
   * back at its own index. `app/session.ts` frees them.
   */
  closedDocs: ClosedDoc[];
  /** The message bar at the bottom-left of the viewer, or nothing. */
  toast: ToastMessage | null;
  addDoc(id: string, name: string, size: number): void;
  setDocStatus(id: string, patch: Partial<DocEntry>): void;
  /** Closes a document: out of `docs`, into `closedDocs`, and one undo entry
   *  ("Closed lorenzo.png") that puts it back where it was. */
  removeDoc(id: string): void;
  /** Lets a held document go — called when its grace period ends, once its
   *  bitmaps and its worker state have been freed. */
  forgetClosedDoc(id: string): void;
  /** Says one thing at the bottom of the viewer. `undoToken` — the token the
   *  action's own `pushUndo` handed back — is what puts an Undo button beside it
   *  and what that button reverses. */
  showToast(text: string, opts?: { undoToken?: UndoToken | null }): void;
  /** Dismisses the message — the one on screen, or only the one with this id
   *  (which is what keeps a 6-second timer from clearing a LATER message). */
  dismissToast(id?: number): void;
  setActive(id: string | null): void;
  setSettings(patch: Partial<Settings>): void;
  setSteps(steps: Step[]): void;
  updateStep(index: number, params: Record<string, unknown>): void;
  setStepEnabled(index: number, enabled: boolean): void;
  setViewer(patch: Partial<ViewerPrefs>): void;
  /** The side panel's width, clamped to what the window can spare. */
  setPanelWidth(w: number): void;
  /** Folds one panel section away, or unfolds it. `next` says which, for a
   *  section whose default is not the stored value (the inspector starts shut,
   *  so `!collapsed[id]` would be a no-op on its first click). */
  toggleSection(id: string, next?: boolean): void;
  toggleOverlay(key: string): void;
  bumpPreview(id: string): void;
  startInspection(id: string, generation: number, previewLanded?: boolean): void;
  setEngineError(message: string | null): void;
  setGlPreview(state: GlPreviewState): void;
  setBatchBusy(busy: boolean): void;
  setAppMode(mode: AppMode): void;
  setSepDefaults(patch: Partial<SepDefaults>): void;
  setDocSep(id: string, patch: Partial<DocSep>): void;
  setSeparation(id: string, next: Separation): void;
  setMockupDefaults(patch: Partial<MockupDefaults>): void;
  setTemplates(templates: TemplateSource[]): void;
  setTemplateStatus(id: string, patch: Partial<TemplateStatus>): void;
  setScenes(scenes: SceneSource[]): void;
  /** The scene's WHOLE status, not a patch: a `SceneStatus` is two fields and every transition
   *  replaces both, so a patch could leave the error of a previous failure on a scene that has
   *  since loaded. A status the store already holds is not written at all (see the setter). */
  setSceneStatus(id: string, status: SceneStatus): void;
  setDocMockup(id: string, patch: Partial<DocMockup>): void;
  /** `opts.undo: false` writes the job WITHOUT an undo entry — for a repair the app
   *  makes on the shop's behalf rather than an edit the shop made (see the setter). */
  setMockupJob(id: string, next: MockupJob, opts?: { undo?: boolean }): void;
  /** One field of the job, merged into whatever the document holds RIGHT NOW. The panel's Backdrop
   *  section writes through this rather than spreading a job it captured a render ago: importing a
   *  scene is hundreds of ms of file read, worker decode and IndexedDB write, and a drag or a colour
   *  change made in that window must not be undone by the backdrop landing on top of it. */
  setMockupBackdrop(id: string, backdrop: Backdrop | null): void;
  setMockupColors(id: string, hexes: string[]): void;
  setBackDoc(id: string, docId: string | null): void;
}

export const useStore = create<AppState>((set, get) => ({
  docs: [], activeId: null, settings: DEFAULT_SETTINGS, steps: defaultDtfPipeline([0, 0, 0]),
  closedDocs: [], toast: null,
  viewer: { background: 'garment', view: 'output', overlays: {}, plate: 'proof', filmView: false, showPrintArea: true, mockupView: 'front', panelWidth: PANEL_DEFAULT, collapsed: {} }, engineError: null, batchBusy: false,
  glPreview: 'cpu',
  appMode: 'dtf', sepDefaults: defaultSepDefaults(),
  mockupDefaults: defaultMockupDefaults(), templates: [], templateStatus: {}, scenes: [], sceneStatus: {},
  addDoc: (id, name, size) => set((st) => ({
    docs: [...st.docs, { id, name, size, status: 'loading', running: false, checks: [], inspecting: false, previewVersion: 0, inspectVersion: 0 }],
    activeId: st.activeId ?? id,
  })),
  setDocStatus: (id, patch) => set((st) => ({ docs: st.docs.map((d) => (d.id === id ? { ...d, ...patch } : d)) })),
  // Nothing in this app used to be undoable, and closing a file is the most
  // expensive × of the lot (audit #14): the entry is SET ASIDE rather than
  // dropped, and the undo entry pushed here puts it back at its own index, with
  // the document that was active before the close active again.
  removeDoc: (id) => {
    const st = get();
    const i = st.docs.findIndex((d) => d.id === id);
    if (i < 0) return;
    const doc = st.docs[i], wasActive = st.activeId;
    // Deliberately WITHOUT a scope, unlike every other entry: a closed file is not
    // an edit to a document's state, it is a change to the list every mode shows —
    // and the document it is about is by definition not the active one any more, so
    // a scoped entry would be the one thing Ctrl+Z could never reach.
    pushUndo(`Closed ${doc.name}`, () => restoreClosedDoc(id, wasActive), closedDocKey(id));
    set((cur) => {
      const docs = cur.docs.filter((d) => d.id !== id);
      // closing the active doc lands on its neighbour (next, else previous), not on the first file
      const neighbour = docs[Math.min(i, docs.length - 1)]?.id ?? null;
      // Capped as well as freed by `session.closeDoc`: a caller that closes
      // documents without going through it (a test, a future batch close) must
      // not retain every entry it ever closed. The undo entries for the ones let
      // go here go with them — an undo that cannot be honoured is worse than none.
      const held = [...cur.closedDocs.filter((c) => c.id !== id), { id, index: i, doc }];
      const kept = held.slice(Math.max(0, held.length - CLOSED_DOCS_MAX));
      for (const c of held.slice(0, held.length - kept.length)) forgetUndo(closedDocKey(c.id));
      return { docs, activeId: cur.activeId === id ? neighbour : cur.activeId, closedDocs: kept };
    });
  },
  forgetClosedDoc: (id) => set((st) => (st.closedDocs.some((c) => c.id === id)
    ? { closedDocs: st.closedDocs.filter((c) => c.id !== id) }
    : st)),
  showToast: (text, opts) => set({ toast: { id: nextToastId(), text, undoToken: opts?.undoToken ?? null } }),
  dismissToast: (id) => set((st) => (st.toast && (id === undefined || st.toast.id === id) ? { toast: null } : st)),
  setActive: (id) => set({ activeId: id }),
  setSettings: (patch) => set((st) => ({ settings: { ...st.settings, ...patch } })),
  setSteps: (steps) => set({ steps }),
  // The `as Step` stays, and it is load-bearing rather than lazy: the step to
  // patch is chosen by a runtime index, so no type parameter can be inferred
  // for its kind, and spreading a `Record<string, unknown>` into a typed
  // params object widens every field to `unknown`. A generic
  // `updateStep<K extends Step['kind']>(index, params)` would need the kind
  // passed at all ~15 call sites purely to satisfy inference. The values do
  // come from typed `Slider`/`Toggle`/`Select` handlers, and `parseSteps`
  // re-validates anything that round-trips through storage.
  updateStep: (index, params) => set((st) => ({
    steps: st.steps.map((s, i) => (i === index ? ({ ...s, params: { ...s.params, ...params } } as Step) : s)),
  })),
  setStepEnabled: (index, enabled) => set((st) => ({
    steps: st.steps.map((s, i) => (i === index ? { ...s, enabled } : s)),
  })),
  setViewer: (patch) => set((st) => ({ viewer: { ...st.viewer, ...patch } })),
  // Clamped here rather than at every call site: the width arrives from a
  // pointer position, an arrow key and a persisted payload, and all three can
  // be out of bounds. The early return keeps a no-op drag (or the re-clamp on
  // every window resize) from touching `viewer` at all, which is what the
  // persistence subscription keys on.
  setPanelWidth: (w) => set((st) => {
    const next = clampPanelWidth(w, viewportWidth());
    return next === st.viewer.panelWidth ? {} : { viewer: { ...st.viewer, panelWidth: next } };
  }),
  toggleSection: (id, next) => set((st) => ({ viewer: { ...st.viewer, collapsed: { ...st.viewer.collapsed, [id]: next ?? !st.viewer.collapsed[id] } } })),
  toggleOverlay: (key) => set((st) => ({ viewer: { ...st.viewer, overlays: { ...st.viewer.overlays, [key]: !st.viewer.overlays[key] } } })),
  bumpPreview: (id) => set((st) => ({ docs: st.docs.map((d) => (d.id === id ? { ...d, previewVersion: d.previewVersion + 1 } : d)) })),
  // One atomic write so the viewer can never observe a half-started inspection:
  // the new token, cleared checks and BOTH halves of the overlay epoch land
  // together, before the inspect request is posted to the worker.
  startInspection: (id, generation, previewLanded) => set((st) => ({
    docs: st.docs.map((d) => (d.id === id
      ? {
          ...d, checks: [], inspecting: true, inspectGeneration: generation,
          inspectVersion: d.inspectVersion + 1,
          // Folded in rather than left to a separate `bumpPreview`: the viewer's
          // overlay cache key is `previewVersion:inspectVersion`, so two writes
          // leave an intermediate epoch in between.
          previewVersion: previewLanded ? d.previewVersion + 1 : d.previewVersion,
        }
      : d)),
  })),
  // First failure wins: the worker's load error is more specific than anything that follows it.
  setEngineError: (message) => set((st) => (message !== null && st.engineError !== null ? st : { engineError: message })),
  setBatchBusy: (busy) => set({ batchBusy: busy }),
  // Guarded so the hook can write the state it believes without churning every subscriber (the
  // banner, the inspector) once per animation frame of a drag.
  setGlPreview: (state) => set((st) => (st.glPreview === state ? st : { glPreview: state })),
  setAppMode: (mode) => set({ appMode: mode }),
  setSepDefaults: (patch) => set((st) => ({ sepDefaults: { ...st.sepDefaults, ...patch } })),
  setDocSep: (id, patch) => set((st) => ({
    docs: st.docs.map((d) => (d.id === id && d.sep ? { ...d, sep: { ...d.sep, ...patch } } : d)),
  })),
  setSeparation: (id, next) => set((st) => ({
    docs: st.docs.map((d) => (d.id === id
      ? { ...d, sep: d.sep ? { ...d.sep, separation: next } : { separation: next, plates: [], checks: [], unmatched: 0, building: false, filmsVersion: 0 } }
      : d)),
  })),
  setMockupDefaults: (patch) => set((st) => ({ mockupDefaults: { ...st.mockupDefaults, ...patch } })),
  // Rebuilding the registry drops the status of every template that is no
  // longer in it: a deleted user template would otherwise keep its (often
  // `error`) status for the life of the session, and an id reused later would
  // inherit it. The object identity is preserved when nothing is pruned, so a
  // plain reload does not churn every subscriber that reads `templateStatus`.
  setTemplates: (templates) => set((st) => {
    const ids = new Set(templates.map((t) => t.template.id));
    const keys = Object.keys(st.templateStatus);
    const kept = keys.filter((id) => ids.has(id));
    if (kept.length === keys.length) return { templates };
    return { templates, templateStatus: Object.fromEntries(kept.map((id) => [id, st.templateStatus[id]])) };
  }),
  setTemplateStatus: (id, patch) => set((st) => ({ templateStatus: { ...st.templateStatus, [id]: { ...(st.templateStatus[id] ?? { state: 'idle' }), ...patch } } })),
  // Prunes the status of every scene no longer listed, exactly as `setTemplates` does: a deleted
  // scene would otherwise keep its (often `error`) status for the life of the session. The object
  // identity is preserved when nothing is pruned, so a plain reload does not churn subscribers.
  setScenes: (scenes) => set((st) => {
    const ids = new Set(scenes.map((s) => s.id));
    const keys = Object.keys(st.sceneStatus);
    const kept = keys.filter((id) => ids.has(id));
    if (kept.length === keys.length) return { scenes };
    return { scenes, sceneStatus: Object.fromEntries(kept.map((id) => [id, st.sceneStatus[id]])) };
  }),
  // Only when it actually differs. Every status write notifies subscribers synchronously and the
  // session's subscription calls straight back into `ensureScene` — an unconditional write of a
  // status the store already holds is how that pair recurses until the stack blows (the same
  // defect the runner's template `setStatus` guards against).
  setSceneStatus: (id, status) => set((st) => {
    const cur = st.sceneStatus[id];
    if (cur && cur.state === status.state && cur.error === status.error) return st;
    return { sceneStatus: { ...st.sceneStatus, [id]: status } };
  }),
  // Both a patch and the seed: a patch carrying a whole `job` creates the entry
  // (that is what `mockupFor` hands over on mode entry), while a patch for a
  // document that has no mockup — a render result landing after the document was
  // reset — is dropped rather than resurrecting half a job.
  setDocMockup: (id, patch) => set((st) => ({
    docs: st.docs.map((d) => {
      if (d.id !== id) return d;
      if (d.mockup) return { ...d, mockup: { ...d.mockup, ...patch } };
      return patch.job ? { ...d, mockup: { colors: [], backDocId: null, rendering: false, clipped: false, skipped: [], ...patch, job: patch.job } } : d;
    }),
  })),
  // Every job write in the app lands here — the panel, a template switch, the registry fallback
  // when a blank is deleted, a canvas drag — so this is where a job is made to satisfy the
  // invariants: rotation in range, one print per area, at most seven of them, `enabled` present,
  // placements inside the ranges the controls offer.
  //
  // Deliberately WITHOUT a template, even though the panel passes one on the way in: the store
  // does not know which blank this is, and must not. A job moved onto a blank with no sleeve
  // keeps its sleeve print — the render reports the area as skipped and the ticket says so, where
  // dropping it would quietly cost the shop the design they chose.
  //
  // `opts.undo: false` is for the writes the APP makes to keep a job legal after
  // something was deleted under it — `deleteUserTemplate` repointing every job off
  // the blank that has gone, `removeScene` clearing a backdrop nothing can render.
  // Those are repairs, not edits: recorded as undoable they hand the shop a Ctrl+Z
  // that puts the document straight back onto the deleted template (the footer then
  // reads "template … is no longer available" while the toast says "Undid Template"),
  // and for a scene a "Backdrop" entry that restores nothing at all.
  setMockupJob: (id, raw, opts) => {
    const st = get();
    const prev = st.docs.find((d) => d.id === id)?.mockup?.job;
    // The diff is taken against the SANITISED job, not the raw one: a width the
    // panel clamped back to where it already was is not a change, and it must not
    // cost the shop an undo press to get through. (The write below sanitises
    // again, against the live scene list — see `writeJob`.)
    if (prev && opts?.undo !== false) {
      const next = sanitizeJob(raw, undefined, st.scenes);
      const fields = changedJobFields(prev, next);
      // Coalesced by which fields moved, so a slider dragged across twenty values
      // is one undo back to where the drag started, while a width edit and a
      // rotation stay two separate steps.
      if (fields.length) pushUndo(jobUndoLabel(fields, prev, next), () => writeJob(id, prev), `job:${id}:${fields.join(',')}`, JOB_SCOPE(id));
    }
    writeJob(id, raw);
  },
  // Through the same sanitiser and the same shape as `setMockupJob` — it IS a job write, of a
  // single field. The live job is read inside `set`, which is what makes it a patch rather than a
  // replacement: `d.mockup.job` here is the store's own, not one a caller closed over.
  setMockupBackdrop: (id, backdrop) => {
    // Undoable like every other job write: choosing a scene (or clearing one) is
    // exactly the kind of change a shop makes to see what it looks like, and the
    // label table has always promised "Backdrop".
    const prev = get().docs.find((d) => d.id === id)?.mockup?.job;
    if (prev && JSON.stringify(prev.backdrop ?? null) !== JSON.stringify(backdrop ?? null)) {
      const was = prev.backdrop ?? null;
      pushUndo('Backdrop', () => writeBackdrop(id, was), `job:${id}:backdrop`, JOB_SCOPE(id));
    }
    writeBackdrop(id, backdrop);
  },
  setMockupColors: (id, hexes) => {
    const prev = get().docs.find((d) => d.id === id)?.mockup?.colors;
    // A colour removed by its chip's × is the audit's #10, and this is what makes
    // it recoverable. Coalesced per document: the swatch row is clickable fast
    // enough that a shop trying four colourways would otherwise fill the stack.
    if (prev && (prev.length !== hexes.length || prev.some((c, i) => c !== hexes[i]))) {
      pushUndo('Garment colours', () => writeColors(id, prev), `colours:${id}`, JOB_SCOPE(id));
    }
    writeColors(id, hexes);
  },
  setBackDoc: (id, docId) => set((st) => ({
    docs: st.docs.map((d) => (d.id === id && d.mockup ? { ...d, mockup: { ...d.mockup, backDocId: docId } } : d)),
  })),
}));

/**
 * Where a mockup-job entry belongs: this document, in mockup mode.
 *
 * The mode is the literal rather than the store's current one, and that is the
 * point — it names the mode whose subscription turns the restore into a render, not
 * the mode the write happened to be made from. A job written from anywhere else is
 * still only undoable where undoing it is visible.
 */
const JOB_SCOPE = (id: string): UndoScope => ({ docId: id, mode: 'mockup' });

/** How many closed documents the store will hold at once. One is the app's own
 *  answer (`session.closeDoc` frees the previous hold on every close); this is
 *  the ceiling for anything that closes documents another way. */
const CLOSED_DOCS_MAX = 8;

/**
 * What the app does with a document Undo has just put back — re-arm whatever was
 * in flight when it was closed. Registered by `app/session.ts`, which owns the
 * three runners; the store cannot call them itself without importing the session
 * that imports it.
 *
 * It is handed the entry AS IT WAS CLOSED, with the in-flight flags still set,
 * because that is the only record of what the close threw away.
 */
let docRestored: ((doc: DocEntry) => void) | null = null;
export function onDocRestored(fn: (doc: DocEntry) => void): void { docRestored = fn; }

/**
 * The same document with nothing left in flight.
 *
 * Every runner drops a result that lands while its document is out of `docs` —
 * correct, because the document may have been freed — so a held entry's `running`,
 * `inspecting`, `building` and `rendering` flags are promises nothing is left to
 * keep. Restored as they were, the file comes back reading "· running" / "Building…"
 * / "Rendering…" for good, with no output, and `ExportBar`'s `canZip` (which asks
 * whether ANY document is busy) disables Export all (ZIP) for every document in the
 * app with nothing on screen to say why. They are cleared here and the work is
 * re-triggered by `onDocRestored`'s subscriber instead.
 */
export function settleDoc(doc: DocEntry): DocEntry {
  if (!doc.running && !doc.inspecting && !doc.sep?.building && !doc.mockup?.rendering && !doc.mockup?.backRendering) return doc;
  return {
    ...doc,
    running: false,
    inspecting: false,
    ...(doc.sep ? { sep: { ...doc.sep, building: false } } : {}),
    ...(doc.mockup ? { mockup: { ...doc.mockup, rendering: false, backRendering: false } } : {}),
  };
}

let toastSeq = 0;
function nextToastId(): number { return ++toastSeq; }

/**
 * The one job write in the app — `setMockupJob` and the undo that reverses it
 * both land here, so an undone job goes through the same sanitiser as the edit
 * that replaced it and cannot push an undo entry of its own (an undo that
 * pushed one would make Ctrl+Z toggle between two jobs for ever).
 *
 * Deliberately WITHOUT a template, even though the panel passes one on the way in: the store
 * does not know which blank this is, and must not. A job moved onto a blank with no sleeve
 * keeps its sleeve print — the render reports the area as skipped and the ticket says so, where
 * dropping it would quietly cost the shop the design they chose.
 */
function writeJob(id: string, raw: MockupJob): void {
  useStore.setState((st) => {
    // The scenes ARE passed, unlike the template: a backdrop naming a scene the shop has deleted
    // names pixels nothing can produce (the render would fail outright), where a print in an area
    // this blank lacks is merely reported as skipped. The store knows the scene list; it does not
    // know which blank this is.
    const next = sanitizeJob(raw, undefined, st.scenes);
    return {
      docs: st.docs.map((d) => (d.id === id
        ? { ...d, mockup: d.mockup ? { ...d.mockup, job: next } : { job: next, colors: [], backDocId: null, rendering: false, clipped: false, skipped: [] } }
        : d)),
    };
  });
}

/** `writeJob` for the one field the Backdrop section owns. The live job is read
 *  inside `set`, which is what makes it a patch rather than a replacement: a scene
 *  import takes hundreds of ms (file read, worker decode, IndexedDB write) and a
 *  drag or a colour change made in that window must not be undone by a job this
 *  render closed over landing on top of it. */
function writeBackdrop(id: string, backdrop: Backdrop | null): void {
  useStore.setState((st) => ({
    docs: st.docs.map((d) => (d.id === id && d.mockup
      ? { ...d, mockup: { ...d.mockup, job: sanitizeJob({ ...d.mockup.job, backdrop }, undefined, st.scenes) } }
      : d)),
  }));
}

/** `writeJob` for the colourway list: the one write both the panel and its undo use. */
function writeColors(id: string, hexes: string[]): void {
  useStore.setState((st) => ({
    docs: st.docs.map((d) => (d.id === id && d.mockup ? { ...d, mockup: { ...d.mockup, colors: hexes } } : d)),
  }));
}

/**
 * Puts a closed document back at the index it was closed from, with the document
 * that was active before the close active again.
 *
 * A no-op once the grace period has freed it: the entry would come back with no
 * bitmaps and no worker state behind it, which is worse than the close it was
 * meant to reverse. (`session.closeDoc` forgets the undo entry when it frees the
 * document, so this is the belt to that braces.)
 */
function restoreClosedDoc(id: string, activeId: string | null): void {
  let held: ClosedDoc | undefined;
  useStore.setState((st) => {
    held = st.closedDocs.find((c) => c.id === id);
    if (!held || st.docs.some((d) => d.id === id)) { held = undefined; return st; }
    const docs = [...st.docs];
    // `settleDoc`, not the entry as it was closed: see its own note. The work it
    // was doing is asked for again below, once the document is back in `docs` where
    // the runners will keep the result.
    docs.splice(Math.min(held.index, docs.length), 0, settleDoc(held.doc));
    return { docs, activeId: activeId ?? held.id, closedDocs: st.closedDocs.filter((c) => c.id !== id) };
  });
  if (held) docRestored?.(held.doc);
}

/** The plain word for each job field an undo entry can be about. */
const JOB_FIELD_LABELS: Record<string, string> = {
  placement: 'Placement', rotationDeg: 'Rotation', templateId: 'Template', garment: 'Garment colour',
  look: 'Printed as', wrinkle: 'Realism', texture: 'Realism', backdrop: 'Backdrop', extraPrints: 'Prints',
};

/**
 * Which top-level fields of a job differ — the coarse key an undo entry coalesces
 * on. Compared field by field as JSON: every field is either a primitive, a
 * three-number colour, a placement of three values or a short list of prints, so
 * this is cheap, and it is exact where an identity check would call every
 * re-sanitised object a change.
 */
export function changedJobFields(a: MockupJob, b: MockupJob): string[] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const out: string[] = [];
  for (const k of keys) {
    const x = (a as unknown as Record<string, unknown>)[k], y = (b as unknown as Record<string, unknown>)[k];
    if (x !== y && JSON.stringify(x ?? null) !== JSON.stringify(y ?? null)) out.push(k);
  }
  return out.sort();
}

/**
 * What the toast calls a job edit: the field's own plain word when one field
 * moved, and the honest generality when several did (a template switch rewrites
 * half the job at once).
 *
 * `placement` is three values in one field, and the one a shop reaches for most
 * is the width — so a write that moved the width and nothing else is called
 * "Width" ("Undid Width"), while a move or a centring stays "Placement".
 */
export function jobUndoLabel(fields: string[], prev?: MockupJob, next?: MockupJob): string {
  if (fields.length === 1 && fields[0] === 'placement' && prev && next) {
    const a = prev.placement, b = next.placement;
    if (a.widthIn !== b.widthIn && a.xIn === b.xIn && a.yIn === b.yIn) return 'Width';
  }
  const labels = [...new Set(fields.map((f) => JOB_FIELD_LABELS[f]).filter(Boolean))];
  return labels.length === 1 ? labels[0] : 'Mockup change';
}
